/**
 * Global event bus for broadcast events (e.g. from WebSocket).
 * Populated by useEvents when it receives events.
 * EventWatchable subscribes to this to refresh on specific events.
 */
type Listener = (payload?: unknown) => void;
type ConnectionStatusListener = (connected: boolean) => void;

const listenersByEvent = new Map<string, Set<Listener>>();
const connectionStatusListeners = new Set<ConnectionStatusListener>();
let isConnected = false;

export function setConnectionStatus(connected: boolean): void {
  if (isConnected === connected) {
    return;
  }
  isConnected = connected;
  for (const cb of connectionStatusListeners) {
    cb(connected);
  }
}

export function subscribeToConnectionStatus(
  cb: ConnectionStatusListener
): () => void {
  connectionStatusListeners.add(cb);
  cb(isConnected);
  return () => {
    connectionStatusListeners.delete(cb);
  };
}

export function subscribe(event: string, listener: Listener): () => void {
  let set = listenersByEvent.get(event);
  if (set === undefined) {
    set = new Set();
    listenersByEvent.set(event, set);
  }
  set.add(listener);
  return () => {
    set?.delete(listener);
  };
}

export function emit(event: string, payload?: unknown): void {
  const set = listenersByEvent.get(event);
  if (set === undefined) {
    return;
  }
  // Dispatch is synchronous and on the caller's stack: `emit` returns only once
  // every listener has been invoked. This is intentional and relied upon (tests
  // and callers assume listeners have at least started by the time `emit`
  // returns), so listeners must be cheap and non-blocking — the event-watchable
  // subscribers here only run a filter check and schedule a background refresh,
  // they never do the refetch inline. Keep it that way; do not add blocking work
  // to a listener.
  //
  // Isolate listeners from one another and from the caller: a broadcast is a
  // best-effort notification, so one subscriber throwing synchronously must
  // not stop the remaining subscribers from running, nor surface as a failure
  // in the code path that emitted the event (e.g. an archive showing a
  // spurious "Failed to archive" toast because a list watchable's handler
  // threw). Each listener runs in its own try/catch; errors are logged, not
  // propagated.
  for (const listener of set) {
    try {
      listener(payload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`broadcast listener for "${event}" threw`, error);
    }
  }
}
